import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { api } from "@/lib/api";
import Login from "@/pages/Login";
import Game from "@/pages/Game";
import AuthCallback from "@/pages/AuthCallback";
// Alpha launch: invite gate removed. ProtectedRoute is defined locally below.
import AdminDiscord from "@/pages/AdminDiscord";
import AdminGM from "@/pages/AdminGM";

function ProtectedRoute({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [state, setState] = useState(location.state?.user ? "auth" : "checking");

  useEffect(() => {
    if (state === "auth") return;
    // Skip auth check if we're currently processing a session_id (AuthCallback handles it)
    if (window.location.hash?.includes("session_id=")) return;
    api.me()
      .then(() => setState("auth"))
      .catch(() => { setState("noauth"); navigate("/login"); });
  }, [state, navigate]);

  if (state === "checking") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050505] text-[#8c8c8c] font-heading tracking-widest">
        <span className="ember-pulse">SUMMONING...</span>
      </div>
    );
  }
  if (state === "noauth") return null;
  return children;
}

function AppRouter() {
  const location = useLocation();
  // Synchronous check to prevent race with ProtectedRoute
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/game" element={<ProtectedRoute><Game /></ProtectedRoute>} />
      <Route path="/admin/discord" element={<ProtectedRoute><AdminDiscord /></ProtectedRoute>} />
      <Route path="/admin/gm" element={<ProtectedRoute><AdminGM /></ProtectedRoute>} />
      <Route path="/" element={<Navigate to="/game" replace />} />
      <Route path="*" element={<Navigate to="/game" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppRouter />
        {/* SVG filter definitions for color-blind accessibility modes.
            Referenced by html.a11y-cb-* classes from `index.css`. */}
        <svg aria-hidden="true" width="0" height="0" style={{ position: "absolute" }}>
          <defs>
            <filter id="a11y-cb-protanopia">
              <feColorMatrix type="matrix" values="0.567 0.433 0 0 0  0.558 0.442 0 0 0  0 0.242 0.758 0 0  0 0 0 1 0" />
            </filter>
            <filter id="a11y-cb-deuteranopia">
              <feColorMatrix type="matrix" values="0.625 0.375 0 0 0  0.7 0.3 0 0 0  0 0.3 0.7 0 0  0 0 0 1 0" />
            </filter>
            <filter id="a11y-cb-tritanopia">
              <feColorMatrix type="matrix" values="0.95 0.05 0 0 0  0 0.433 0.567 0 0  0 0.475 0.525 0 0  0 0 0 1 0" />
            </filter>
          </defs>
        </svg>
        <Toaster
          position="bottom-right"
          theme="dark"
          duration={1800}
          toastOptions={{
            style: {
              background: "#0a0a0a",
              border: "1px solid #262626",
              color: "#E2DFD8",
              fontSize: "12px",
              padding: "8px 10px",
              minHeight: "unset",
              borderRadius: 0,
            },
            className: "font-body",
          }}
        />
      </BrowserRouter>
    </div>
  );
}
